/**
 * A project's pull requests from the row of record: whose turn it is, checks, the task and the
 * agent that made it, with Approve, Request changes and Merge acting as the signed-in person
 * through their linked account under the project's merge rules. And the rules themselves, for
 * owners.
 */

import type { PullRequest, PullRequestTurn, PullRequestsPolicy } from '@aioe/shared';
import { CodeMerge, CodePullRequest } from '@gravity-ui/icons';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { useAgentName } from '../agentNames.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark, Tick, Toggle } from './ui.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { PullRequestFilesSheet } from './pullRequestFiles.tsx';

type Row = PullRequest & { key: string; turn: PullRequestTurn };

const STATE_TEXT: Record<PullRequest['state'], string> = { open: 'Open', draft: 'Draft', merged: 'Merged', closed: 'Closed' };

export function PullRequestsPanel({ projectId, canWrite = false }: { projectId: string; canWrite?: boolean }) {
  const tone = VIEW_TONES.projects!;
  const list = useLoad(() => routes.pullRequests(projectId), [projectId], 30_000);
  const rows = [...(list.data?.items ?? [])].sort((a, b) => (a.turn === 'yours' ? 0 : 1) - (b.turn === 'yours' ? 0 : 1) || (a.state === 'open' || a.state === 'draft' ? 0 : 1) - (b.state === 'open' || b.state === 'draft' ? 0 : 1) || b.updatedAt.localeCompare(a.updatedAt));
  const yours = rows.filter((p) => p.turn === 'yours').length;
  return (
    <ListGroup
      title="Pull requests"
      count={rows.length || undefined}
      description={yours ? `${yours} waiting on you. Recorded by members' workbenches and the provider; act on them as yourself.` : "Recorded by members' workbenches and the provider; act on them as yourself."}
      empty={list.data && rows.length === 0 && !list.error ? <Empty tone={tone} icon={<CodePullRequest />} compact title="No pull requests yet" body="A member's workbench records them as it watches the project's repositories." /> : undefined}
    >
      {list.error ? (
        <ListBlock>
          <ErrorNote error={list.error} />
        </ListBlock>
      ) : null}
      {rows.map((p) => (
        <PullRequestRow key={p.key} p={p} projectId={projectId} canWrite={canWrite} onChange={list.reload} />
      ))}
    </ListGroup>
  );
}

function PullRequestRow({ p, projectId, canWrite, onChange }: { p: Row; projectId: string; canWrite: boolean; onChange: () => void }) {
  const agentName = useAgentName();
  const [files, setFiles] = useState(false);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [asking, setAsking] = useState<'request-changes' | 'comment'>();
  const [body, setBody] = useState('');
  const settled = p.state === 'merged' || p.state === 'closed';
  const act = async (action: 'approve' | 'request-changes' | 'comment' | 'merge', text?: string) => {
    setBusy(action);
    setError(undefined);
    try {
      await routes.actPullRequest(projectId, p.key, { action, ...(text ? { body: text } : {}) });
      setAsking(undefined);
      setBody('');
      onChange();
    } catch (e) {
      const b = (e as { body?: { message?: string } }).body;
      setError(b?.message ?? (e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  const checks = p.checks.state === 'failing' ? `checks failed${p.checks.failed.length ? `: ${p.checks.failed.join(', ')}` : ''}` : p.checks.state === 'pending' ? 'checks running' : p.checks.state === 'passing' ? 'checks passed' : undefined;
  const approvals = p.reviewers.filter((r) => r.state === 'approved').length;
  const waiting = p.reviewers.filter((r) => r.state === 'requested').map((r) => r.login);
  return (
    <ListRow
      icon={<CodePullRequest />}
      title={
        <a href={p.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
          #{p.number} {p.title}
        </a>
      }
      description={
        <>
          {STATE_TEXT[p.state]} · {p.repository}
          {p.branch && ` · ${p.branch}`}
          {` · by ${p.author.login}`}
          {p.by.actor && ` (${agentName(p.by.actor)})`}
          {p.task && ` · ${p.task}`}
          {checks && ` · ${checks}`}
          {p.conflicts && ' · conflicts'}
          {approvals > 0 && ` · ${approvals} ${approvals === 1 ? 'approval' : 'approvals'}`}
          {waiting.length > 0 && ` · waiting on ${waiting.join(', ')}`}
          {` · ${timeAgo(p.updatedAt)}`}
          {p.provider === 'github' && (
            <>
              {' · '}
              <button type="button" className="cursor-(--cursor-interactive) text-accent hover:underline" onClick={() => setFiles(true)}>
                Changed files
              </button>
            </>
          )}
        </>
      }
      {...(p.turn === 'yours' && !settled ? { status: <StatusMark state="working" label="Your turn" /> } : {})}
      {...(!settled
        ? {
            control: (
              <>
                <Button size="sm" variant="secondary" isDisabled={Boolean(busy)} onPress={() => void act('approve')}>
                  {busy === 'approve' ? 'Approving…' : 'Approve'}
                </Button>
                <RowMenu
                  label={`More for #${p.number}`}
                  items={[
                    { id: 'request-changes', label: 'Request changes', disabled: Boolean(busy) },
                    { id: 'merge', label: busy === 'merge' ? 'Merging…' : 'Merge', disabled: Boolean(busy) },
                  ]}
                  onAction={(k) => (k === 'merge' ? void act('merge') : setAsking(asking === 'request-changes' ? undefined : 'request-changes'))}
                />
              </>
            ),
          }
        : {})}
    >
      {asking || error || files ? (
        <div className="grid gap-2">
          {asking ? (
            <div className="flex flex-wrap items-center gap-2">
              <input className={`${inputClass} min-w-0 max-w-xl flex-1 basis-56`} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What should change" aria-label="What should change" />
              <Button size="sm" variant="ghost" onPress={() => (setAsking(undefined), setBody(''))}>
                Cancel
              </Button>
              <Button size="sm" variant="danger-soft" isDisabled={!body.trim() || Boolean(busy)} onPress={() => void act('request-changes', body.trim())}>
                Send
              </Button>
            </div>
          ) : null}
          <ErrorNote error={error} />
          {files && <PullRequestFilesSheet projectId={projectId} prKey={p.key} title={`#${p.number} ${p.title}`} canComment={canWrite && !settled} onClose={() => setFiles(false)} onCommented={onChange} />}
        </div>
      ) : undefined}
    </ListRow>
  );
}

const METHOD_TEXT: Record<'merge' | 'squash' | 'rebase', string> = { merge: 'Merge commit', squash: 'Squash', rebase: 'Rebase' };

/** The organisation's merge rules for the project; owners change them. */
export function MergeRulesPanel({ projectId, policy, owner, onChange }: { projectId: string; policy?: PullRequestsPolicy; owner: boolean; onChange: () => void }) {
  const current: PullRequestsPolicy = policy ?? { requiredApprovals: 1, allowSelfMerge: false, mergeMethods: ['merge', 'squash', 'rebase'], requireChecks: true, agentsMayMerge: false };
  const [draft, setDraft] = useState<PullRequestsPolicy>(current);
  const [error, setError] = useState<string>();
  const dirty = JSON.stringify(draft) !== JSON.stringify(current);
  const save = async () => {
    setError(undefined);
    try {
      await routes.setProjectSettings(projectId, { pullRequests: draft });
      onChange();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  // A rule is a switch in the draft; the group's one Save keeps them all.
  const flag = (k: 'allowSelfMerge' | 'requireChecks' | 'agentsMayMerge', label: string) => <ListRow title={label} control={<Toggle checked={draft[k]} isDisabled={!owner} label={label} onChange={(v) => setDraft({ ...draft, [k]: v })} />} />;
  return (
    <ListGroup title="Merge rules" description="What a workbench refuses to merge, and what the organisation lets through.">
      <ListRow
        icon={<CodeMerge />}
        title="Approvals needed before a merge"
        control={<ChoiceSelect label="Approvals needed before a merge" className="w-24" isDisabled={!owner} value={String(draft.requiredApprovals)} options={{ '0': '0', '1': '1', '2': '2', '3': '3' }} onChange={(v) => setDraft({ ...draft, requiredApprovals: Number(v) })} />}
      />
      {flag('requireChecks', 'Checks must pass')}
      {flag('allowSelfMerge', 'An author may merge their own')}
      {flag('agentsMayMerge', 'Agents may merge')}
      <ListRow title="Ways to merge" description="At least one stays allowed.">
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          {(['merge', 'squash', 'rebase'] as const).map((m) => (
            <Tick
              key={m}
              checked={draft.mergeMethods.includes(m)}
              isDisabled={!owner || (draft.mergeMethods.length === 1 && draft.mergeMethods.includes(m))}
              onChange={(v) => setDraft({ ...draft, mergeMethods: v ? [...draft.mergeMethods, m] : draft.mergeMethods.filter((x) => x !== m) })}
            >
              {METHOD_TEXT[m]}
            </Tick>
          ))}
        </div>
      </ListRow>
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {owner && dirty ? (
        <ListBlock className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onPress={() => setDraft(current)}>
            Discard
          </Button>
          <Button size="sm" variant="primary" onPress={() => void save()}>
            Save rules
          </Button>
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
